import React, { FC, useState, useMemo } from "react";
import Chip from "@mui/material/Chip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";

interface PercentageFilterChipProps {
  value?: number;
  onChange: (value: number) => void;
  id?: string;
  "data-testid"?: string;
}

export const PercentageFilterChip: FC<PercentageFilterChipProps> = ({
  value,
  onChange,
  id = "percentage",
  "data-testid": dataTestId = id,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const percentageOptions = useMemo(() => {
    const options = [];
    for (let i = 5; i <= 100; i += 5) {
      options.push(i);
    }
    return options;
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (percentage: number) => {
    onChange(percentage);
    handleClose();
  };

  const chipLabel = useMemo(() => {
    if (value) {
      return `${value}%`;
    }
    return "Select %";
  }, [value]);

  return (
    <Box>
      <Chip
        id={`${id}-chip`}
        data-testid={`${dataTestId}-chip`}
        label={chipLabel}
        onClick={handleClick}
        variant="filled"
        color={value ? "primary" : "default"}
        sx={{
          "& .MuiChip-label": {
            fontWeight: value ? 500 : 400,
          },
        }}
      />
      <Menu
        id={`${id}-menu`}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: { sx: { maxHeight: 300, width: "150px" } },
        }}
      >
        {percentageOptions.map((percentage) => (
          <MenuItem
            key={percentage}
            id={`${id}-option-${percentage}`}
            data-testid={`${dataTestId}-option-${percentage}`}
            onClick={() => handleMenuItemClick(percentage)}
            selected={value === percentage}
            sx={{
              py: 1,
              minHeight: "36px",
              fontWeight: value === percentage ? 600 : 400,
            }}
          >
            <Typography variant="body2">{percentage}%</Typography>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};
